import { toMillis } from "./dateUtils";

function pad2(value) {
  return String(value).padStart(2, "0");
}

function formatDateToDisplayKst(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "-";

  const formatter = new Intl.DateTimeFormat("ko-KR", {
    timeZone: "Asia/Seoul",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });

  const parts = formatter.formatToParts(date);
  const map = {};

  for (const part of parts) {
    map[part.type] = part.value;
  }

  const dayPeriod = map.dayPeriod || (Number(map.hour) < 12 ? "오전" : "오후");

  return `${Number(map.year)}. ${Number(map.month)}. ${Number(
    map.day
  )}. ${dayPeriod} ${pad2(map.hour)}:${pad2(map.minute)}:${pad2(map.second)}`;
}

export function formatEventKST(value) {
  const ms = toMillis(value);
  if (!ms) return "-";

  return formatDateToDisplayKst(new Date(ms));
}

export function normalizeEventType(value, fallbackType = "") {
  const v = String(value || "").trim().toUpperCase();
  if (v) return v;

  const type = String(fallbackType || "").trim();

  if (type.includes("SOS 해제")) return "SOS_DISMISS";
  if (type.includes("심박수 이상 해제")) return "HEART_RATE_HIGH_DISMISS";
  if (type.includes("심박수 이상")) return "HEART_RATE_HIGH";
  if (type === "SOS") return "SOS";
  if (type.includes("민트캠 켜짐")) return "MINTCAM_ON";
  if (type.includes("민트캠 꺼짐")) return "MINTCAM_OFF";

  return type.toUpperCase();
}

export function isWatchEvent(row) {
  const eventType = normalizeEventType(row?.eventType, row?.type);

  if (
    eventType === "SOS" ||
    eventType === "SOS_DISMISS" ||
    eventType === "HEART_RATE_HIGH" ||
    eventType === "HEART_RATE_HIGH_DISMISS"
  ) {
    return true;
  }

  const sourceText = [
    row?.type,
    row?.source,
    row?.deviceType,
    row?.message,
    row?.eventSource,
    row?.deviceName,
  ]
    .filter(Boolean)
    .join(" ")
    .toUpperCase();

  return (
    sourceText.includes("WATCH") ||
    sourceText.includes("민트워치".toUpperCase())
  );
}

export function typeBadgeClass(row) {
  // row 객체 또는 문자열(구버전 호환) 모두 허용
  const v =
    typeof row === "string"
      ? row.toUpperCase()
      : String(row?.eventType || "").toUpperCase();

  if (v === "SOS") {
    return "bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300";
  }

  if (v === "SOS_DISMISS") {
    return "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300";
  }

  if (v === "HEART_RATE_HIGH") {
    return "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300";
  }

  if (v === "HEART_RATE_HIGH_DISMISS") {
    return "bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300";
  }

  // 민트캠(켜짐/꺼짐) — 상단 '민트캠' 카드 색(주황)과 통일
  if (v === "MINTCAM_ON" || v === "MINTCAM_OFF") {
    return "bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300";
  }

  // AI 안전장구 — 위반 종류별로 상단 카드 색과 통일(안전모=파랑 / 조끼=보라 / 안전대=빨강)
  if (v === "PPE_VIOLATION") {
    const r =
      typeof row === "string"
        ? ""
        : String(row?.eventReason || row?.rawEventReason || "").toUpperCase();
    const t = typeof row === "string" ? "" : String(row?.type || "");

    if (r === "NO_HELMET" || t.includes("안전모")) {
      return "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300";
    }
    if (r === "NO_VEST" || t.includes("안전조끼") || t.includes("조끼")) {
      return "bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300";
    }
    if (
      r === "NO_HARNESS" ||
      r === "NO_BELT" ||
      t.includes("안전대") ||
      t.includes("하네스") ||
      t.includes("벨트")
    ) {
      return "bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300";
    }
    return "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300";
  }

  return "bg-gray-100 text-gray-700 dark:bg-white/10 dark:text-gray-300";
}

// AI 안전장구(PPE) 위반 종류별 아이콘
export function getPpeIcon(row) {
  const r = String(row?.eventReason || row?.rawEventReason || "").toUpperCase();
  const t = String(row?.type || "");
  if (r === "NO_HELMET" || t.includes("안전모")) return "⛑️";
  if (r === "NO_VEST" || t.includes("안전조끼") || t.includes("조끼")) return "🦺";
  if (
    r === "NO_HARNESS" ||
    r === "NO_BELT" ||
    t.includes("안전대") ||
    t.includes("벨트") ||
    t.includes("하네스")
  )
    return "🪢";
  return "⚠️";
}

// AI 안전장구 위반 종류별 아이콘 이미지+색 (이벤트 화면 SummaryCard와 동일하게 통일).
// 위험감지/확인 모달 등에서 이 정보로 같은 아이콘을 그린다.
export function getPpeIconInfo(row) {
  const r = String(row?.eventReason || row?.rawEventReason || "").toUpperCase();
  const t = String(row?.type || "");
  if (r === "NO_HELMET" || t.includes("안전모"))
    return { src: "/assets/icons/ppe_helmet.png", color: "#2563EB" };
  if (r === "NO_VEST" || t.includes("안전조끼") || t.includes("조끼"))
    return { src: "/assets/icons/ppe_vest.png", color: "#8B5CF6" };
  if (
    r === "NO_HARNESS" ||
    r === "NO_BELT" ||
    t.includes("안전대") ||
    t.includes("벨트") ||
    t.includes("하네스")
  )
    return { src: "/assets/icons/ppe_harness.png", color: "#DC2626" };
  return null;
}

export function normalizeTypeLabel(row) {
  const v = String(row?.eventType || "").toUpperCase();

  if (v === "SOS") return "SOS";
  if (v === "SOS_DISMISS") return "SOS 해제";
  if (v === "HEART_RATE_HIGH") return "심박수 이상";
  if (v === "HEART_RATE_HIGH_DISMISS") return "심박수 이상 해제";
  if (v === "MINTCAM_ON") return "민트캠 켜짐";
  if (v === "MINTCAM_OFF") return "민트캠 꺼짐";

  // AI 안전장구 미착용: 타입 컬럼엔 아이콘 없이 라벨만(색으로 구분)
  if (v === "PPE_VIOLATION") {
    return String(
      row?.eventReasonLabel || row?.type || "안전장구 미착용"
    ).replace(/^⚠️\s*/, "");
  }

  return row?.type || "이벤트";
}

export function buildNote(row) {
  const eventType = String(row?.eventType || "").toUpperCase();

  if (eventType === "SOS_DISMISS" || eventType === "HEART_RATE_HIGH_DISMISS") {
    return `${row?.dismissedBy || "-"} / ${row?.dismissReason || "-"}`;
  }

  if (eventType === "MINTCAM_ON" || eventType === "MINTCAM_OFF") {
    const reason = row?.eventReasonLabel || row?.eventReason || "-";
    const source = row?.eventSource || row?.triggerType || "-";

    return `${reason} / ${source}`;
  }

  // AI 안전장구 위반 — 비고를 한글로 통일(원본 테이블명 'MintcamPpeEventLogs' 노출 방지)
  if (eventType === "PPE_VIOLATION") {
    return "AI 자동감지";
  }

  return row?.source || "-";
}

// 안전장구 위반의 '확인(처리)' 내역 — 이벤트 이력의 '처리(확인)' 열에 사용.
// 누가(by) 무슨 조치(note)를 언제(at) 남겼는지. PPE 위반이 아니면 null.
export function getAckInfo(row) {
  if (String(row?.eventType || "").toUpperCase() !== "PPE_VIOLATION") return null;
  const done = row?.acknowledged === true || Number(row?.acknowledged) === 1;
  const fp = row?.isFalsePositive === true || Number(row?.isFalsePositive) === 1;
  return {
    done,
    fp,
    by: String(row?.acknowledgedBy || "").trim(),
    note: String(row?.acknowledgedNote || "").trim(),
    at: String(row?.acknowledgedAt || "").trim(),
    fpBy: String(row?.falsePositiveBy || "").trim(),
    fpAt: String(row?.falsePositiveAt || "").trim(),
  };
}

export function getBestEventTimeValue(row) {
  return (
    row?.timestamp ||
    row?.createdAt ||
    row?.updatedAt ||
    row?.Timestamp ||
    row?.CreatedAt ||
    row?.UpdatedAt ||
    row?.eventAt ||
    row?.EventAt ||
    row?.time ||
    row?.Time ||
    row?.CreatedAtText ||
    null
  );
}

export function getDisplayTime(row) {
  const best = getBestEventTimeValue(row);
  const ms = toMillis(best);

  if (ms) return formatEventKST(ms);

  const rawDisplay = String(row?.displayTime || "").trim();
  if (rawDisplay) return rawDisplay;

  return "-";
}

export function extractEventRows(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.data)) return data.data;

  return [];
}

export function normalizeEventRows(rows) {
  return (Array.isArray(rows) ? rows : [])
    .map((row) => {
      const bestTime = getBestEventTimeValue(row);
      const timeMs = toMillis(bestTime);

      return {
        ...row,
        eventType: normalizeEventType(row?.eventType, row?.type),
        _timeMs: timeMs,
      };
    })
    .sort((a, b) => (b._timeMs || 0) - (a._timeMs || 0));
}